import { State } from "EmployerDashboard/store";
import { createSelector } from "reselect";

import { TIERED_CONTRIBUTIONS_SORTING_ORDER } from "Common/Constants";

import { companyPolicyApi } from "./companyPolicy.api";
import { CompanyContributionsUpdate, EmployeeContribution, ContributionRole, CompanyContribution } from "./companyPolicy.types";

// One instance, because a fresh array on every call defeats the memoisation of any
// createSelector that takes this as an input.
const TIER_ROLES = [null, ...TIERED_CONTRIBUTIONS_SORTING_ORDER] as CompanyContribution["role"][];

/**
 * Select contribution tiers
 * currently, they are fixed, but we are working on adding "flexible tiers"
 * and when those are done, this will select the custom tiers on this policy
 * @see https://docs.google.com/document/d/1q2ctFUvFmVeJG4QwXpUmcw-aZLSnryE7ntMcHJTLgZw/edit?tab=t.0#heading=h.gzrzehgtozhl
 * */
export const selectTierRoles = () => TIER_ROLES;

/** Select the entire EmployerCompanyPolicy for a given companyPolicyId */
export const selectCompanyPolicyData = (companyPolicyId: string) => (state: State) =>
  companyPolicyApi.endpoints.getCompanyPolicy.select({ companyPolicyId })(state)?.data;

export const selectCompanyContributions = (companyPolicyId: string) => (state: State) =>
  companyPolicyApi.endpoints.getCompanyPolicy.select({ companyPolicyId })(state)?.data?.companyContributions ?? [];

/**
 * Drops employees whose policy is already cancelled. Future-dated cancellations
 * remain visible since they are still active today.
 */
const filterActiveEmployees = (employees: EmployeeContribution[]): EmployeeContribution[] => {
  const now = Date.now();
  return employees.filter((e) => !e.cancelledAt || new Date(e.cancelledAt).getTime() > now);
};

/** Select the list of employee contributions on a given company policy */
export const selectEmployeeContributionsOnCompanyPolicy = (companyPolicyId: string) =>
  createSelector(
    (state: State) => selectCompanyPolicyData(companyPolicyId)(state),
    (policy) => filterActiveEmployees(policy?.employeeContributions ?? []),
  );

/**
 * Select all contributions for a given company policy.
 */
export const selectContributionsForCompanyPolicy = (companyPolicyId: string) => (state: State) =>
  companyPolicyApi.endpoints.getCompanyPolicy.select({ companyPolicyId })(state)?.data?.companyContributions ?? [];

/**
 * Select the initial form state for updating company contributions.
 * This replicates the logic previously in `selectInitialValues`, but now
 * directly uses the API query selectors and minimal composition.
 */
export const selectContributionUpdatesFromCompanyPolicy = (companyPolicyId: string) =>
  createSelector(
    (state: State) => selectCompanyPolicyData(companyPolicyId)(state),
    (state) => selectTierRoles(state),
    (companyPolicy, contributionTiers): CompanyContributionsUpdate | undefined => {
      if (!companyPolicy) return undefined;

      const contributionMethod: CompanyContributionsUpdate["contributionMethod"] = companyPolicy.hasTieredContributions ? "tiered" : "nonTiered";

      const { typeOfContribution, companyContributions } = companyPolicy;
      const employeeContributions = filterActiveEmployees(companyPolicy.employeeContributions ?? []);

      // We initialize contributions for each role, even if they don't exist
      // this makes managing form state easy since we have assurance that all tiers are defined
      const initializeContribution = (role: CompanyContribution["role"]): CompanyContribution => ({
        role,
        id: null,
        active: true,
        employeePercentage: null,
        employeeFixedAmount: null,
        dependentPercentage: null,
        dependentFixedAmount: null,
      });

      const mappedContributions: CompanyContribution[] = contributionTiers.map((tierName) => {
        const matchingContributionByRole = companyContributions.find((c) => c.role === tierName);
        return {
          ...initializeContribution(tierName),
          ...matchingContributionByRole,
        };
      });

      return {
        contributionMethod,
        typeOfContribution,
        employeeContributions,
        companyContributions: mappedContributions,
      };
    },
  );

function parseNumber(val: unknown): number {
  const p = parseFloat(String(val));
  return Number.isNaN(p) ? 0 : p;
}

interface CostBreakdown {
  companyEmployee: number;
  employeeEmployee: number;
  companyDependents: number;
  employeeDependents: number;
}

/**
 * Break down combined costs into employee-only and dependents-only portions
 * proportionally based on their share of the total premium.
 */
function breakDownCosts(employeePremium: number, dependentPremium: number, companyCost: number, employeeCost: number): CostBreakdown {
  const totalPremium = employeePremium + dependentPremium;
  if (totalPremium === 0) {
    return {
      companyEmployee: 0,
      employeeEmployee: 0,
      companyDependents: 0,
      employeeDependents: 0,
    };
  }

  const employeeRatio = employeePremium / totalPremium;
  const dependentsRatio = dependentPremium / totalPremium;

  const companyCostEmployee = companyCost * employeeRatio;
  const companyCostDependents = companyCost * dependentsRatio;
  const employeeCostEmployee = employeePremium - companyCostEmployee;
  const employeeCostDependents = dependentPremium - companyCostDependents;

  return {
    companyEmployee: companyCostEmployee,
    employeeEmployee: employeeCostEmployee,
    companyDependents: companyCostDependents,
    employeeDependents: employeeCostDependents,
  };
}

export interface ComputedEmployeeCostDifference extends EmployeeContribution {
  /** Total difference for employee (new - original) */
  employeeDiff: number;
  /** Total difference for company (new - original) */
  companyDiff: number;
  /** Updated company cost based on current values */
  companyCost: number;
  /** Updated employee cost based on current values */
  employeeCost: number;
  /** Original breakdown */
  originalCompanyCostEmployee: number;
  originalEmployeeCostEmployee: number;
  originalCompanyCostDependents: number;
  originalEmployeeCostDependents: number;
  /** New breakdown */
  newCompanyCostEmployee: number;
  newEmployeeCostEmployee: number;
  newCompanyCostDependents: number;
  newEmployeeCostDependents: number;
  dependentsCount: number;
}

export interface AggregatedCostDifferences {
  companyShare: number;
  employeeShare: number;
  totalCost: number;
  employeeShareDiff: number;
  companyShareDiff: number;
}

export interface CostDifferencesResult {
  newCosts: ComputedEmployeeCostDifference[];
  aggregated: AggregatedCostDifferences;
}

/**
 * A selector that computes cost differences between the initial state and the current form state.
 * It uses the companyPolicyId from state, and takes currentValues as argument.
 *
 * Usage:
 * const { newCosts, aggregated } = useSelector((state: State) => selectCostDifferences(state, currentValues));
 */
export const selectCostDifferences = createSelector(
  (state: State) => state.companyPolicyId,
  (state: State, _currentValues: CompanyContributionsUpdate) => selectContributionUpdatesFromCompanyPolicy(state.companyPolicyId)(state),
  (_state: State, currentValues: CompanyContributionsUpdate) => currentValues,
  (_companyPolicyId, initialFormState, currentValues): CostDifferencesResult | undefined => {
    if (!initialFormState || !currentValues) {
      return undefined;
    }

    const currentContributionType = currentValues.typeOfContribution;
    const mapRoleToCurrentContribution: Record<ContributionRole, CompanyContribution> = currentValues.companyContributions.reduce(
      (acc, contribution) => {
        acc[contribution.role] = contribution;
        return acc;
      },
      {} as Record<ContributionRole, CompanyContribution>,
    );

    const newCosts: ComputedEmployeeCostDifference[] = currentValues.employeeContributions.map((emp, index) => {
      const initialEmp = initialFormState.employeeContributions[index];

      const contribution = mapRoleToCurrentContribution[emp.role] || mapRoleToCurrentContribution.null; // fallback if role changed or not found

      // Original totals
      const originalCompanyCost = initialEmp.companyCost;
      const originalEmployeeCost = initialEmp.employeeCost;
      const { totalCost, employeePremium, dependentPremium, dependents } = emp;

      const dependentsCount = dependents.filter((d) => d.isEnrollingMedical).length;

      let newCompanyCost = 0;
      let newEmployeeCost = 0;

      // Calculate new costs based on type_of_contribution and updated contributions
      switch (currentContributionType) {
        case "fixed": {
          const efa = parseNumber(contribution.employeeFixedAmount);
          const dfa = parseNumber(contribution.dependentFixedAmount);
          const companyForEmployee = Math.min(employeePremium, efa);
          const companyForDependents = Math.min(dependentPremium, dfa);
          newCompanyCost = companyForEmployee + companyForDependents;
          newEmployeeCost = totalCost - newCompanyCost;
          break;
        }

        case "percentage":
        case "percent_of_base_plan": {
          const ep = parseNumber(contribution.employeePercentage);
          const dp = parseNumber(contribution.dependentPercentage);
          const companyForEmployee = employeePremium * (ep / 100);
          const companyForDependents = dependentPremium * (dp / 100);
          newCompanyCost = companyForEmployee + companyForDependents;
          newEmployeeCost = totalCost - newCompanyCost;
          break;
        }

        default:
          // If unknown, fallback to original costs
          newCompanyCost = originalCompanyCost;
          newEmployeeCost = originalEmployeeCost;
          break;
      }

      const employeeDiff = newEmployeeCost - originalEmployeeCost;
      const companyDiff = newCompanyCost - originalCompanyCost;

      // Break down original costs
      const originalBreakdown = breakDownCosts(employeePremium, dependentPremium, originalCompanyCost, originalEmployeeCost);

      // Break down new costs
      const newBreakdown = breakDownCosts(employeePremium, dependentPremium, newCompanyCost, newEmployeeCost);

      return {
        ...emp,
        employeeDiff,
        companyDiff,
        companyCost: newCompanyCost,
        employeeCost: newEmployeeCost,
        originalCompanyCostEmployee: originalBreakdown.companyEmployee,
        originalEmployeeCostEmployee: originalBreakdown.employeeEmployee,
        originalCompanyCostDependents: originalBreakdown.companyDependents,
        originalEmployeeCostDependents: originalBreakdown.employeeDependents,
        newCompanyCostEmployee: newBreakdown.companyEmployee,
        newEmployeeCostEmployee: newBreakdown.employeeEmployee,
        newCompanyCostDependents: newBreakdown.companyDependents,
        newEmployeeCostDependents: newBreakdown.employeeDependents,

        dependentsCount,
      };
    });

    const aggregated = newCosts.reduce<AggregatedCostDifferences>(
      (acc, cost) => {
        acc.companyShare += cost.companyCost;
        acc.employeeShare += cost.employeeCost;
        acc.totalCost += cost.totalCost;
        acc.employeeShareDiff += cost.employeeDiff;
        acc.companyShareDiff += cost.companyDiff;
        return acc;
      },
      {
        companyShare: 0,
        employeeShare: 0,
        totalCost: 0,
        employeeShareDiff: 0,
        companyShareDiff: 0,
      },
    );

    return {
      newCosts,
      aggregated,
    };
  },
);
